import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { format, differenceInHours } from "date-fns";
import { 
    useTheme, 
    useMediaQuery, 
    Box, 
    Typography, 
    Button, 
    TextField, 
    CircularProgress,
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
} from '@mui/material';
import useAuthCheck from 'hooks/useAuthCheck';
import { setTimeMode } from "state";
import { AIRLINE_COUNTRY_CODE } from 'state/constants';
import api from 'state/axios';
import { getFlagUrl, formatNumber } from 'state/helpers';
import { buildPermissionMap, hasRight } from 'state/checkPermission';
import FlexBetween from 'components/FlexBetween'; 
import FlexCenter from 'components/FlexCenter'; 
import DeleteConfirm from 'components/DeleteConfirm';
import QuantityControl from 'components/QuantityControl'; 
import VietnamFlag from "img/VietnamFlag.png";
import UnitedNationsFlag from "img/UnitedNationsFlag.png";


const InvoiceDialog = ({ 
    open, 
    onClose, 
    selectedInvoiceId, 
    crewCode, 
    crewName, 
    selectedFlights, 
    crewMembers,
    selectedProducts, 
    onInvoiceSaved 
}) => {
    useAuthCheck();
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);
    const timeMode = useSelector((state) => state.timemode);
    const permissionMap = useMemo(() => buildPermissionMap(user?.permissions), [user?.permissions]);
    const params = useSelector((state) => state.params);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);    

    const [isProcessingTimeOut, setIsProcessingTimeOut] = useState(true);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [deleteType, setDeleteType] = useState(null); 

    const [products, setProducts] = useState([]);
    const [productList, setProductList] = useState([]);
    const [quantities, setQuantities] = useState({});
    const [priceType, setPriceType] = useState('domesticprice');
        
    const paramsMap = useMemo(
        () => new Map(params?.map(p => [p.param, p.value]) ?? []),
        [params]
    );
    const EDIT_INVOICE_OFFSET_HOURS = Number(paramsMap.get("EDIT_INVOICE_OFFSET_HOURS")) || 168;
    const AIRLINE_NATIONAL_CODE = paramsMap.get("AIRLINE_COUNTRY_CODE") || "VN";   
    const CARRIER_NATIONAL_CODE = AIRLINE_NATIONAL_CODE || AIRLINE_COUNTRY_CODE;
    const flagUrl = timeMode === "UTC" 
        ? (getFlagUrl("un") || UnitedNationsFlag) 
        : (getFlagUrl(CARRIER_NATIONAL_CODE) || VietnamFlag);
    
    
    useEffect(() => {
        const checkFlightDate = () => {
            const now = new Date();
            const isTimeOut = selectedFlights.some(f => {                
                return differenceInHours(now, new Date(f.flightDate)) > EDIT_INVOICE_OFFSET_HOURS
            });
            setIsProcessingTimeOut(isTimeOut);
        };
    
        checkFlightDate();
    }, [selectedFlights, EDIT_INVOICE_OFFSET_HOURS]);

    const resetDialogState = useCallback(() => {
        setProducts([]);
        setProductList([]);
        setQuantities({});
        setPriceType('domesticprice');     
        setError(null);   
    }, [])

    const fetchProducts = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/products/active');
            const initialProducts = res.data;

            if (selectedFlights.length > 0) {
                const sectorType = selectedFlights[0]?.sectorType;
                
                if (sectorType === 'International') {
                    setPriceType("internationalprice");
                } else if (sectorType === 'Domestic') {
                    setPriceType("domesticprice");
                }

                initialProducts.forEach(product => {                    
                    if (sectorType === 'International') {
                        product.price = product.internationalPrice;
                    } else if (sectorType === 'Domestic') {
                        product.price = product.domesticPrice;
                    }
                });
            }
            
            const finalProducts = selectedProducts || initialProducts;                        
            const initialQuantities = finalProducts.reduce((acc, product) => {
                acc[product._id] = {
                    preorderQuantity: product.preorderQuantity || 0,
                    directQuantity: product.directQuantity || 0,
                };
                return acc;
            }, {});

            setProducts(initialProducts);
            setProductList(finalProducts);
            setQuantities(initialQuantities);
        } catch (err) {
            setError(err.message);
            toast.error(err.message || "Failed to fetch products!");
        } finally {
            setLoading(false);
        }
    }, [selectedFlights, selectedProducts]);

    useEffect(() => {
        if (open) fetchProducts();
        else resetDialogState();
    }, [open, fetchProducts, resetDialogState]);

    const totalAmounts = useMemo(() => {
        let preorderAmount = 0; 
        let directAmount = 0;
        let totalAmount = 0;
        let preorderCommission = 0;
        let directCommission = 0;
        let totalCommission = 0;
        let payableAmount = 0;

        productList.forEach((product) => {
            const { preorderQuantity, directQuantity } = quantities[product._id] || {};
            preorderAmount += (product.price || 0) * (preorderQuantity || 0);
            directAmount += (product.price || 0) * (directQuantity || 0);
            preorderCommission += (product.price || 0) * (preorderQuantity || 0) * (product.preorderCommissionRate || 0);
            directCommission += (product.price || 0) * (directQuantity || 0) * (product.directCommissionRate || 0);
        });
        totalAmount = preorderAmount + directAmount;
        // preorderCommission = preorderAmount * PREORDER_COMMISSION_RATE;
        // directCommission = directAmount * DIRECT_COMMISSION_RATE;
        totalCommission = preorderCommission + directCommission;
        payableAmount = directAmount - directCommission;

        return {
            preorderAmount,
            directAmount,
            totalAmount,
            payableAmount,
            preorderCommission,
            directCommission,
            totalCommission,
        };
    }, [productList, quantities]);

    const handleSaveInvoice = async () => {
        const { 
            preorderAmount,
            directAmount,
            totalAmount,
            payableAmount,
            preorderCommission,
            directCommission,
            totalCommission,
        } = totalAmounts;

        const crewCount = crewMembers && crewMembers.length > 0 ? crewMembers.length : 0;
        const eachCrewPreorderCommission = crewCount > 0 ? preorderCommission / crewCount : 0;
        const eachCrewDirectCommission = crewCount > 0 ? directCommission / crewCount : 0;
        const eachCrewTotalCommission = crewCount > 0 ? totalCommission / crewCount : 0;

        const payload = {
            invoiceId: selectedInvoiceId,
            crewCode: crewCode, 
            crewName: crewName,
            selectedFlights,
            crewMembers,
            products: productList.map(product => ({
                ...product,
                preorderQuantity: quantities[product._id]?.preorderQuantity || 0,
                directQuantity: quantities[product._id]?.directQuantity || 0,
            })),
            preorderAmount,
            directAmount,
            totalAmount,
            preorderCommission,
            directCommission,
            totalCommission,
            payableAmount,
            crewCount,
            eachCrewPreorderCommission,
            eachCrewDirectCommission,
            eachCrewTotalCommission,
            userId: user._id,
        };
    
        try {
            const url = selectedInvoiceId 
                ? `/inflightsales/update/${payload.invoiceId}`
                : '/inflightsales/invoice';
            const method = selectedInvoiceId ? 'put' : 'post';            
            await api[method](url, payload);
            onInvoiceSaved(selectedFlights);
            onClose();
        } catch (error) {
            setError(error);
            toast.error(error.message || "Failed to save invoice!");
        }
    };   
        
    const handleDeleteClick = () => {        
        setDeleteType('single');
        setConfirmDialogOpen(true);
    };
    
    const handleDeleteInvoice = async () => {
        try {
            if (selectedInvoiceId) {
                await api.delete(`/inflightsales/delete/${selectedInvoiceId}`);
                
                onInvoiceSaved(selectedFlights);
                onClose();
            }
        } catch (error) {
            setError(error);
            toast.error(error.message || "Failed to delete invoice!");
        }
    };

    const handlePriceChange = (productId, price) => {
        const updatedProductList = productList.map(product => {
            if (product._id === productId) {
                return { ...product, price }; 
            }
            return product;
        });
        setProductList(updatedProductList);
    };

    const handleQuantityChange = (productId, type, newQuantity) => {
        setQuantities(prev => ({
            ...prev,
            [productId]: { 
                ...prev[productId], 
                [type]: Math.max(0, newQuantity) 
            },
        }));
    };

    const togglePriceType = () => {
        const updatedProductList = productList.map((product) => {
            const matchProduct = products.find(item => item._id === product._id);

            if (matchProduct) {
                if (priceType === 'domesticprice') {
                    product.price = matchProduct.internationalPrice;
                } else {
                    product.price = matchProduct.domesticPrice;
                }
            }
            return product;
        });
        setProductList(updatedProductList);
        setPriceType(priceType === 'domesticprice' ? 'internationalprice' : 'domesticprice');
    };

    const reloadProducts = () => {
        const updatedProductList = products.map((product) => {
            const existingProduct = productList.find(item => item._id === product._id);
            const currentQuantities = quantities[product._id] || { preorderQuantity: 0, directQuantity: 0 };
        
            return {
                ...product,
                price: priceType === 'domesticprice' ? product.domesticPrice : product.internationalPrice,
                preorderQuantity: existingProduct ? currentQuantities.preorderQuantity : 0,
                directQuantity: existingProduct ? currentQuantities.directQuantity : 0,
            };
        });
      
        setProductList(updatedProductList);
    };
    
    const handleCloseDialog = () => {
        setPriceType('domesticprice');
        setProductList([]);
        setQuantities({});        
        onClose();
    };
    
    const getTime = (flight, timeMode, field) => 
        timeMode === 'UTC' ? flight[field] : flight[`${field}LocalBase`];


    return (
        <Dialog 
            open={open} 
            onClose={handleCloseDialog}
            sx={{ '& .MuiDialog-paper': { minWidth: isMobile ? '90%' : '55%' } }}
        >
            <DialogTitle borderBottom={`1px solid ${theme.palette.neutral.dark}`}>
                <FlexBetween pb={1} borderBottom={`1px solid ${theme.palette.neutral.dark}`}>
                    <Typography 
                        variant="h6" 
                        fontWeight="bold" 
                        color="primary"                                                
                    >
                        INVOICE: {crewCode} - {crewName}
                    </Typography>
                    <Box onClick={() => dispatch(setTimeMode())}>
                        <img
                            alt="Flag"
                            width={25}
                            height={25}
                            src={flagUrl}
                            style={{ 
                                cursor: "pointer", 
                                objectFit: "cover", 
                                borderRadius: "50%",
                                transition: "opacity 0.3s",
                            }}
                            onMouseOver={(e) => e.currentTarget.style.opacity = '1'}
                            onMouseOut={(e) => e.currentTarget.style.opacity = '0.9'}
                        />
                    </Box>
                </FlexBetween>                
                <Box p={0.5}>
                    {selectedFlights.length > 0 ? (
                        selectedFlights.map((flight, idx) => (                            
                        <FlexBetween key={idx} p={0.3}>
                            <Typography width="20px" textAlign="center" color="primary">{flight.flightNo}</Typography>
                            <Typography width="44px" textAlign="center">
                                {timeMode === 'UTC' 
                                    ? format(flight.flightDate, 'ddMMMyy')
                                    : format(flight.flightDateLocalBase, 'ddMMMyy')
                                }
                            </Typography>
                            <Typography width="25px" textAlign="center">
                                {getTime(flight, timeMode, 'etd')}
                            </Typography>
                            <Typography width="20px" textAlign="center">{flight.dep}</Typography>
                            <Typography width="20px" textAlign="center">{flight.arr}</Typography>
                            <Typography width="25px" textAlign="center">
                                {getTime(flight, timeMode, 'eta')}
                            </Typography> 
                            <Typography width="4px" textAlign="center">{flight.pax}</Typography>
                        </FlexBetween>
                    ))) : (
                        <Typography color="textSecondary">No flight selected for this invoice.</Typography>
                    )}
                </Box>
                
                <FlexBetween pt={1} borderTop={`1px solid ${theme.palette.neutral.dark}`}>   
                    <Typography>
                        Preorder: {formatNumber(totalAmounts.preorderAmount)}
                    </Typography>   
                    <Typography>
                        Preorder Commission: {formatNumber(totalAmounts.preorderCommission)}
                    </Typography>                 
                </FlexBetween>   

                <FlexBetween pt={0.5}>   
                    <Typography>
                        Direct: {formatNumber(totalAmounts.directAmount)}
                    </Typography>                    
                    <Typography color="primary">
                        Direct Commission: {formatNumber(totalAmounts.directCommission)}
                    </Typography>
                    <Typography color="primary">
                        Payable: {formatNumber(totalAmounts.payableAmount)}
                    </Typography> 
                </FlexBetween>
            </DialogTitle>

            <DialogContent>
                {loading ? (
                    <FlexCenter sx={{ height: "100%", width: "100%", mt: 1 }}>
                        <CircularProgress />
                    </FlexCenter>
                ) : (productList.map((product, idx) => (
                        <Box 
                            key={idx} 
                            py={1} 
                            borderBottom={`1px solid ${theme.palette.neutral.dark}`}                            
                        > 
                            <FlexBetween gap={2}>
                                <Typography 
                                    color="primary"
                                    textAlign="left"
                                    sx={{ width: isMobile ? '250px' : '370px' }} 
                                >
                                    {idx + 1}. {product.productName}
                                </Typography>
                                <Typography width="20px" textAlign="center">{product.unit}</Typography>
                                <TextField
                                    id={`price-${idx}`}
                                    variant="standard"
                                    type="number"
                                    label="Price"                                    
                                    name="price"                                 
                                    onChange={(e) => {
                                        handlePriceChange(product._id, Math.max(0, parseInt(e.target.value, 10))); 
                                    }}
                                    value={product.price}
                                    InputProps={{
                                        inputProps: { 
                                            readOnly: true, 
                                            min: 0, 
                                            step: 1000, 
                                            style: { textAlign: 'center' } 
                                        } 
                                    }}
                                    sx={{ width: '80px' }}
                                />
                            </FlexBetween>
                        
                            <FlexBetween gap={2} py={0.5}>         
                                <FlexBetween>
                                    <Typography width="56px" textAlign="center">Preorder</Typography>
                                    <QuantityControl 
                                        productId={product._id} 
                                        type="preorderQuantity" 
                                        quantity={quantities[product._id]?.preorderQuantity || 0} 
                                        handleQuantityChange={handleQuantityChange} 
                                    />
                                </FlexBetween>
                                
                                <FlexBetween>
                                    <Typography width="50px" textAlign="center">Direct</Typography>
                                    <QuantityControl 
                                        productId={product._id} 
                                        type="directQuantity" 
                                        quantity={quantities[product._id]?.directQuantity || 0} 
                                        handleQuantityChange={handleQuantityChange} 
                                    />
                                </FlexBetween>
                            </FlexBetween>                        
                        </Box>
                    ))
                )}                
            </DialogContent>
            <DialogActions>             
                {error && <Typography color="error">{error.message}</Typography>}
                {products && (
                    <Box display="flex" gap={1}>                        
                        <Button
                            variant="outlined" 
                            color="third"
                            disabled={loading || isProcessingTimeOut}
                            onClick={() => {
                                if (!hasRight(permissionMap, "Invoices", "reload")) return;
                                reloadProducts();
                            }}
                        >
                            Reload
                        </Button>
                        <Button
                            variant="outlined" 
                            color="third"
                            disabled={loading || isProcessingTimeOut}
                            sx={{ display: 'none' }}
                            onClick={() => {
                                if (!hasRight(permissionMap, "Invoices", "price")) return;
                                togglePriceType();
                            }}
                        >
                            {priceType === 'domesticprice' ? 'Int Price' : 'Dom Price'}
                        </Button>
                    </Box>                    
                )}
                {selectedInvoiceId && (
                    <Button 
                        variant="outlined" 
                        color="error" 
                        disabled={loading || isProcessingTimeOut}
                        onClick={() => {
                            if (!hasRight(permissionMap, "Invoices", "delete")) return;
                            handleDeleteClick();
                        }}
                    >
                        Delete
                    </Button>
                )} 
                <Button 
                    variant="outlined" 
                    disabled={loading || isProcessingTimeOut} 
                    onClick={() => {
                        if (!hasRight(permissionMap, "Invoices", "create")) return;
                        handleSaveInvoice();
                    }}
                >
                    Save
                </Button>
                <Button 
                    variant="outlined" 
                    color="secondary" 
                    onClick={handleCloseDialog}
                >
                    Close
                </Button>
            </DialogActions>

            <DeleteConfirm
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={handleDeleteInvoice}
                deleteType={deleteType}
            />
        </Dialog>
    );
};

export default InvoiceDialog;
